<template>
  <div class="contract-form">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ isEdit ? '编辑合同' : '新增合同' }}</span>
          <el-button @click="handleCancel">取消</el-button>
        </div>
      </template>

      <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        class="contract-form-content"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="客户" prop="customerId">
              <div class="customer-selector">
                <el-input
                  v-model="formData.customerName"
                  placeholder="请选择客户"
                  readonly
                  style="width: 100%"
                  @click="openCustomerPicker"
                >
                  <template #suffix>
                    <el-icon v-if="formData.customerId" class="clear-icon" @click.stop="clearCustomer">
                      <Close />
                    </el-icon>
                    <el-icon class="arrow-icon">
                      <ArrowDown />
                    </el-icon>
                  </template>
                </el-input>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="合同编号">
              <el-input v-model="formData.contractNumber" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="合同类型" prop="contractType">
              <el-select v-model="formData.contractType" placeholder="请选择合同类型" style="width: 100%">
                <el-option
                  v-for="item in contractTypeOptions"
                  :key="item.dataCode"
                  :label="item.dataName"
                  :value="item.dataCode"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="合同金额(元)" prop="contractAmount">
              <el-input-number
                v-model="formData.contractAmount"
                :min="0"
                :precision="2"
                :step="100"
                placeholder="请输入合同金额"
                style="width: 100%"
              />
              <span v-if="amountUnit" class="amount-unit">{{ amountUnit }}</span>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="合同开始日期" prop="startDate">
              <el-date-picker
                v-model="formData.startDate"
                type="date"
                placeholder="请选择开始日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                :disabled-date="getStartDateDisabled"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="合同结束日期" prop="endDate">
              <el-date-picker
                v-model="formData.endDate"
                type="date"
                placeholder="请选择结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                :disabled-date="getEndDateDisabled"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="合同备注" prop="remark">
          <el-input
            v-model="formData.remark"
            type="textarea"
            :rows="4"
            placeholder="请输入合同备注（必填）"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="合同图片">
          <ImageUpload 
            v-model="formData.contractImages"
            :max-count="10"
            :business-type="'CONTRACT'"
            :module="'合同管理'"
          />
        </el-form-item>

        <el-form-item label="合同附件">
          <AttachmentUpload 
            ref="attachmentUploadRef"
            v-model="formData.contractAttachments"
            :max-count="10"
            :business-type="'CONTRACT'"
            :module="'合同管理'"
            :show-download="false"
          />
        </el-form-item>
      </el-form>

      <!-- 客户选择弹窗 -->
      <el-dialog
        v-model="customerPickerVisible"
        title="选择客户"
        width="600px"
        :close-on-click-modal="false"
        class="customer-picker-dialog"
      >
        <div class="customer-picker-content">
          <!-- 搜索框 -->
          <div class="customer-picker-search">
            <el-input
              v-model="customerPickerKeyword"
              placeholder="客户姓名/电话"
              clearable
              @keyup.enter="handleCustomerSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="handleCustomerSearch">搜索</el-button>
          </div>

          <!-- 客户列表 -->
          <div class="customer-picker-list" v-loading="customerPickerLoading">
            <div
              v-for="item in customerPickerList"
              :key="item.id"
              class="customer-picker-item"
              :class="{ active: formData.customerId === item.id }"
              @click="selectCustomer(item)"
            >
              <div class="customer-item-main">
                <div class="customer-name">{{ item.customerName }}</div>
                <div class="customer-meta">
                  <span v-if="item.contactName">{{ item.contactName }}</span>
                  <span v-else class="no-contact">暂无联系人</span>
                  <span v-if="item.contactPhone" class="phone">{{ item.contactPhone }}</span>
                </div>
              </div>
              <el-icon v-if="formData.customerId === item.id" class="check-icon">
                <Check />
              </el-icon>
            </div>
            <el-empty
              v-if="!customerPickerLoading && customerPickerList.length === 0"
              description="暂无客户数据"
              :image-size="100"
            />
          </div>

          <!-- 分页 -->
          <div class="customer-picker-pagination" v-if="customerPickerTotal > 0">
            <el-pagination
              v-model:current-page="customerPickerPage"
              v-model:page-size="customerPickerPageSize"
              :total="customerPickerTotal"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleCustomerPageSizeChange"
              @current-change="handleCustomerPageChange"
            />
          </div>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="closeCustomerPicker">取消</el-button>
            <el-button type="primary" @click="confirmCustomerSelection" :disabled="!formData.customerId">
              确定
            </el-button>
          </div>
        </template>
      </el-dialog>

      <div class="form-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="info" :loading="saving" @click="handleSaveDraft">保存草稿</el-button>
        <div style="position: relative; display: inline-block;">
          <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
            个人用户点提交审批直接通过
          </div>
          <el-button class="submit-approval-btn" :loading="submitting" @click="handleSubmit">提交审批</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Close, ArrowDown, Search, Check } from '@element-plus/icons-vue'
import {
  getContractDetail,
  createContract,
  updateContract,
  getCustomerList,
  checkNeedApproval
} from '../api/salesman'
import { getDictDataByCode } from '../api/common'
import { batchUploadFiles } from '../utils/fileUtils'
import ImageUpload from '../components/ImageUpload.vue'
import AttachmentUpload from '../components/AttachmentUpload.vue'

const route = useRoute()
const router = useRouter()

// 注入关闭标签页的方法
const closeTab = inject('closeTab', null)

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

const formRef = ref(null)
const isEdit = computed(() => !!route.query.id && !route.query.correctId)
const isCorrectMode = computed(() => !!route.query.correctId)
const contractId = computed(() => {
  if (isCorrectMode.value && route.query.correctId) {
    return Number(route.query.correctId)
  }
  return route.query.id ? Number(route.query.id) : null
})

const formData = reactive({
  customerId: null,
  customerName: '',
  contractNumber: '',
  contractType: '',
  contractAmount: null,
  startDate: '',
  endDate: '',
  content: '',
  remark: '',
  contractImages: [],
  attachmentImages: [],
  contractAttachments: [] // 附件列表，格式：[{url: string, fileName: string, size: number}]
})

const formRules = {
  customerId: [{ required: true, message: '请选择客户', trigger: 'change' }],
  contractType: [{ required: true, message: '请选择合同类型', trigger: 'change' }],
  contractAmount: [{ required: true, message: '请输入合同金额', trigger: 'blur' }],
  startDate: [
    { required: true, message: '请选择开始日期', trigger: 'blur' }
  ],
  endDate: [
    { required: true, message: '请选择结束日期', trigger: 'blur' }
  ],
  remark: [{ required: true, message: '请输入合同备注', trigger: 'blur' }]
}

const contractTypeOptions = ref([])
const saving = ref(false)
const submitting = ref(false)
const attachmentUploadRef = ref(null)

// 客户选择弹窗相关
const customerPickerVisible = ref(false)
const customerPickerKeyword = ref('')
const customerPickerList = ref([])
const customerPickerPage = ref(1)
const customerPickerPageSize = ref(20)
const customerPickerTotal = ref(0)
const customerPickerLoading = ref(false)

// 金额单位显示
const amountUnit = computed(() => {
  if (!formData.contractAmount || formData.contractAmount <= 0) {
    return ''
  }
  const amount = Number(formData.contractAmount)
  if (amount >= 10000) {
    const wan = amount / 10000
    if (wan % 1 === 0) {
      return `${parseInt(wan)}万`
    }
    return `${wan.toFixed(1).replace(/\.0$/, '')}万`
  } else if (amount >= 1000) {
    const qian = amount / 1000
    if (qian % 1 === 0) {
      return `${parseInt(qian)}千`
    }
    return `${qian.toFixed(1).replace(/\.0$/, '')}千`
  }
  return ''
})

// 生成合同编号
const generateContractNumber = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const random = String(Math.floor(Math.random() * 10000)).padStart(8, '0')
  return `HT-${year}${month}${day}-${random}`
}

// 加载合同类型字典
const loadContractTypes = async () => {
  try {
    const res = await getDictDataByCode('contract_type')
    if (res.code === '000000' && res.data) {
      contractTypeOptions.value = res.data
      // 新增模式下，默认选择第一项
      if (!isEdit.value && contractTypeOptions.value.length > 0 && !formData.contractType) {
        formData.contractType = contractTypeOptions.value[0].dataCode
      }
    }
  } catch (error) {
    console.error('加载合同类型失败:', error)
  }
}

// 打开客户选择弹窗
const openCustomerPicker = () => {
  customerPickerVisible.value = true
  customerPickerKeyword.value = ''
  customerPickerPage.value = 1
  customerPickerList.value = []
  customerPickerTotal.value = 0
  loadCustomerPicker()
}

// 关闭客户选择弹窗
const closeCustomerPicker = () => {
  customerPickerVisible.value = false
}

// 客户搜索
const handleCustomerSearch = () => {
  customerPickerPage.value = 1
  loadCustomerPicker()
}

// 加载客户列表
const loadCustomerPicker = async () => {
  customerPickerLoading.value = true
  try {
    const res = await getCustomerList({
      customerName: customerPickerKeyword.value.trim() || undefined,
      page: customerPickerPage.value,
      pageSize: customerPickerPageSize.value
    })
    if (res && res.code === '000000') {
      // 兼容不同的返回格式：list 或 records
      const data = res.data || {}
      customerPickerList.value = data.list || data.records || []
      customerPickerTotal.value = data.total || 0
    } else {
      // API 返回错误
      const errorMsg = res?.message || res?.msg || '加载客户列表失败'
      ElMessage.error(errorMsg)
      customerPickerList.value = []
      customerPickerTotal.value = 0
    }
  } catch (error) {
    console.error('加载客户列表失败:', error)
    // 确保错误消息是字符串
    const errorMsg = error?.message || error?.toString() || '加载客户列表失败'
    ElMessage.error(typeof errorMsg === 'string' ? errorMsg : '加载客户列表失败')
    customerPickerList.value = []
    customerPickerTotal.value = 0
  } finally {
    customerPickerLoading.value = false
  }
}

// 分页大小变化
const handleCustomerPageSizeChange = (size) => {
  customerPickerPageSize.value = size
  customerPickerPage.value = 1
  loadCustomerPicker()
}

// 页码变化
const handleCustomerPageChange = (page) => {
  customerPickerPage.value = page
  loadCustomerPicker()
}

// 选择客户
const selectCustomer = (customer) => {
  formData.customerId = customer.id
  formData.customerName = customer.customerName
  // 触发表单验证
  formRef.value?.validateField('customerId')
}

// 确认选择客户
const confirmCustomerSelection = () => {
  if (!formData.customerId) {
    ElMessage.warning('请选择客户')
    return
  }
  closeCustomerPicker()
}

// 清除所选客户
const clearCustomer = () => {
  formData.customerId = null
  formData.customerName = ''
}

// 开始日期禁用规则
const getStartDateDisabled = (date) => {
  if (!formData.endDate) {
    return false
  }
  try {
    const endDate = new Date(formData.endDate)
    if (isNaN(endDate.getTime())) {
      return false
    }
    // 禁用晚于结束日期的日期
    return date.getTime() > endDate.getTime()
  } catch (error) {
    console.error('日期比较错误:', error)
    return false
  }
}

// 结束日期禁用规则
const getEndDateDisabled = (date) => {
  if (!formData.startDate) {
    return false
  }
  try {
    const startDate = new Date(formData.startDate)
    if (isNaN(startDate.getTime())) {
      return false
    }
    // 禁用早于开始日期的日期
    return date.getTime() < startDate.getTime()
  } catch (error) {
    console.error('日期比较错误:', error)
    return false
  }
}

// 加载合同详情
const loadContractDetail = async () => {
  if (!contractId.value) return
  
  try {
    const res = await getContractDetail(contractId.value)
    if (res.code === '000000' && res.data) {
      const data = res.data
      formData.customerId = data.customerId
      formData.customerName = data.customerName || ''
      formData.contractNumber = data.contractNumber || data.contractNo || ''
      formData.contractType = data.contractType || ''
      formData.contractAmount = data.contractAmount ? Number(data.contractAmount) : null
      formData.startDate = data.startDate || ''
      formData.endDate = data.endDate || ''
      formData.content = data.content || ''
      formData.remark = data.remark || ''
      // 处理图片数据
      let images = []
      if (data.contractImages) {
        if (Array.isArray(data.contractImages)) {
          images = data.contractImages
        } else if (typeof data.contractImages === 'string') {
          try {
            const parsed = JSON.parse(data.contractImages)
            images = Array.isArray(parsed) ? parsed : [parsed]
          } catch {
            // 在catch块中，确保contractImages是字符串
            if (typeof data.contractImages === 'string') {
              images = data.contractImages.split(',').map(s => s.trim()).filter(Boolean)
            }
          }
        }
      }
      // 确保图片URL为完整地址
      const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      formData.contractImages = images.map(img => {
        if (!img) return ''
        if (img.startsWith('http://') || img.startsWith('https://')) return img
        return img.startsWith('/') ? baseURL + img : `${baseURL}/${img}`
      }).filter(Boolean)
      
      formData.attachmentImages = data.attachmentImages || []
      
      // 处理附件数据（从后端获取文件名和大小）
      let attachments = []
      // 优先使用 contractAttachmentInfos（包含文件大小信息）
      if (data.contractAttachmentInfos && Array.isArray(data.contractAttachmentInfos) && data.contractAttachmentInfos.length > 0) {
        attachments = data.contractAttachmentInfos.map(att => {
          let url = att.url
          if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          return {
            url: url || att.url,
            fileName: att.fileName || att.originalFileName || '附件',
            originalFileName: att.originalFileName || att.fileName || '附件',
            size: att.size || 0
          }
        })
      } else if (data.contractAttachments) {
        // 兼容旧格式：如果 contractAttachmentInfos 不存在，使用 contractAttachments
        if (Array.isArray(data.contractAttachments)) {
          attachments = await Promise.all(data.contractAttachments.map(async (att) => {
            if (typeof att === 'string') {
              let url = att
              if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
              }
              // 从后端获取文件名
              let fileName = url.split('/').pop() || '附件'
              let size = 0
              try {
                const { getFileName } = await import('../api/file')
                const fileNameRes = await getFileName(url)
                if (fileNameRes.code === '000000') {
                  // 优先使用 data 字段，如果没有则使用 msg 字段
                  fileName = fileNameRes.data || fileNameRes.msg || fileName
                }
              } catch (error) {
                console.warn('获取文件名失败:', error)
              }
              return { url, fileName, originalFileName: fileName, size }
            }
            // 如果已经是对象，确保有 originalFileName
            if (att.originalFileName) {
              return att
            }
            return { ...att, originalFileName: att.fileName || att.name || '附件' }
          }))
        } else if (typeof data.contractAttachments === 'string') {
          try {
            const parsed = JSON.parse(data.contractAttachments)
            const urls = Array.isArray(parsed) ? parsed : [parsed]
            attachments = await Promise.all(urls.map(async (url) => {
              if (typeof url === 'string') {
                let fullUrl = url
                if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                  fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
                }
                let fileName = fullUrl.split('/').pop() || '附件'
                let size = 0
                try {
                  const { getFileName } = await import('../api/file')
                  const fileNameRes = await getFileName(fullUrl)
                  if (fileNameRes.code === '000000') {
                    // 优先使用 data 字段，如果没有则使用 msg 字段
                    fileName = fileNameRes.data || fileNameRes.msg || fileName
                  }
                } catch (error) {
                  console.warn('获取文件名失败:', error)
                }
                return { url: fullUrl, fileName, originalFileName: fileName, size }
              }
              return url
            }))
          } catch {
            // 在catch块中，确保contractAttachments是字符串
            if (typeof data.contractAttachments === 'string') {
              const urlArray = data.contractAttachments.split(',').map(s => s.trim()).filter(Boolean)
              attachments = await Promise.all(urlArray.map(async (url) => {
              let fullUrl = url
              if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
              }
              let fileName = fullUrl.split('/').pop() || '附件'
              let size = 0
              try {
                const { getFileName } = await import('../api/file')
                const fileNameRes = await getFileName(fullUrl)
                if (fileNameRes.code === '000000') {
                  // 优先使用 data 字段，如果没有则使用 msg 字段
                  fileName = fileNameRes.data || fileNameRes.msg || fileName
                }
              } catch (error) {
                console.warn('获取文件名失败:', error)
              }
              return { url: fullUrl, fileName, originalFileName: fileName, size }
            }))
            } else {
              attachments = []
            }
          }
        }
      }
      formData.contractAttachments = attachments
    } else {
      ElMessage.error(res.message || '加载合同详情失败')
    }
  } catch (error) {
    console.error('加载合同详情失败:', error)
    ElMessage.error('加载合同详情失败')
  }
}


// 验证日期先后顺序
const validateDateRange = () => {
  if (!formData.startDate || !formData.endDate) {
    return true // 如果日期为空，由必填校验处理
  }
  if (formData.startDate > formData.endDate) {
    ElMessage.error('开始日期必须早于或等于结束日期')
    return false
  }
  return true
}

// 验证表单
const validateForm = async () => {
  if (!formRef.value) {
    return false
  }
  try {
    await formRef.value.validate()
    // 校验日期先后顺序
    if (!validateDateRange()) {
      return false
    }
    return true
  } catch (error) {
    return false
  }
}

// 存草稿
const handleSaveDraft = async () => {
  if (!(await validateForm())) return
  if (saving.value) return

  saving.value = true
  submitting.value = true
  try {
    // 上传所有待上传的图片
    const pendingImages = formData.contractImages.filter(img => 
      img && typeof img === 'object' && img.status === 'pending' && img.file
    )
    
    if (pendingImages.length > 0) {
      ElMessage.info(`正在上传 ${pendingImages.length} 张图片...`)
      try {
        const uploadedImages = await batchUploadFiles(
          pendingImages,
          'CONTRACT',
          '合同管理'
        )
        
        const uploadedUrls = []
        for (let i = 0; i < formData.contractImages.length; i++) {
          const image = formData.contractImages[i]
          if (image && typeof image === 'object' && image.status === 'pending' && image.file) {
            const uploaded = uploadedImages.find(u => {
              return u.fileName === (image.fileName || image.file.name)
            })
            if (uploaded) {
              let imageUrl = uploaded.url
              if (!imageUrl.startsWith('http')) {
                const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
                imageUrl = imageUrl.startsWith('/') ? baseURL + imageUrl : `${baseURL}/${imageUrl}`
              }
              if (image.url && image.url.startsWith('blob:')) {
                URL.revokeObjectURL(image.url)
              }
              uploadedUrls.push(imageUrl)
            }
          } else {
            uploadedUrls.push(typeof image === 'string' ? image : (image.url || image))
          }
        }
        formData.contractImages = uploadedUrls
      } catch (error) {
        ElMessage.error(`图片上传失败: ${error.message}`)
        throw error
      }
    }
    
    // 上传所有待上传的附件（使用组件的方法，自动显示进度条）
    if (attachmentUploadRef.value) {
      try {
        const uploadedAttachments = await attachmentUploadRef.value.uploadAll()
        // 更新 formData.contractAttachments（组件内部已经更新了，这里确保同步）
        formData.contractAttachments = uploadedAttachments || formData.contractAttachments
      } catch (error) {
        ElMessage.error(`附件上传失败: ${error.message}`)
        throw error
      }
    }
    
    const requestData = {
      contractNumber: formData.contractNumber,
      customerId: formData.customerId,
      contractType: formData.contractType,
      contractAmount: formData.contractAmount,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      content: formData.content || '',
      remark: formData.remark || '',
      // 合同图片：始终传数组，空数组表示清空旧图片
      contractImages: Array.isArray(formData.contractImages)
        ? formData.contractImages
        : [],
      attachmentImages: formData.attachmentImages,
      // 合同附件：始终传数组，空数组表示清空旧附件
      contractAttachments: Array.isArray(formData.contractAttachments)
        ? formData.contractAttachments.map(att => att.url || '').filter(url => url)
        : [],
      contractStatus: 'draft',
      action: 'draft'
    }

    let res
    // 修正模式下创建新数据
    if (isEdit.value && contractId.value && !isCorrectMode.value) {
      requestData.contractId = contractId.value
      res = await updateContract(requestData)
    } else {
      res = await createContract(requestData)
    }

    if (res.code === '000000') {
      ElMessage.success('保存成功')
      // 如果是新增合同或修正模式存草稿，直接跳转到列表页，不停留在详情页
      // 构建标签路径，用于关闭标签页
      let fullPath = route.path
      if (route.path === '/home/salesman-contract-form' && route.query && route.query.id) {
        fullPath = `${route.path}?id=${route.query.id}`
      } else if (route.path === '/home/salesman-contract-form') {
        fullPath = route.path
      } else if (route.query && Object.keys(route.query).length > 0) {
        const queryString = Object.keys(route.query)
          .map(key => `${key}=${encodeURIComponent(route.query[key])}`)
          .join('&')
        fullPath = `${route.path}?${queryString}`
      }
      
      // 跳转到列表页并关闭当前标签
      router.push({
        path: '/home/salesman-contract',
        query: { refresh: 'true', contractStatus: 'draft' }
      }).finally(() => {
        if (closeTab) {
          closeTab(fullPath)
        }
      })
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (error) {
    console.error('保存草稿失败:', error)
    ElMessage.error('保存失败')
  } finally {
    saving.value = false
    submitting.value = false
  }
}

// 提交审批
const handleSubmit = async () => {
  if (!(await validateForm())) return
  if (submitting.value) return

  // 先检查是否需要审批
  if (contractId.value) {
    try {
      const checkRes = await checkNeedApproval('CONTRACT', contractId.value)
      if (checkRes.code === '000000' && checkRes.data === false) {
        await ElMessageBox.alert('该流程无需审批，直接通过！', '提示', {
          type: 'info',
          confirmButtonText: '知道了'
        })
      }
    } catch (error) {
      console.error('检查审批节点失败:', error)
    }
  }

  submitting.value = true
  try {
    // 上传所有待上传的图片
    const pendingImages = formData.contractImages.filter(img => 
      img && typeof img === 'object' && img.status === 'pending' && img.file
    )
    
    if (pendingImages.length > 0) {
      ElMessage.info(`正在上传 ${pendingImages.length} 张图片...`)
      try {
        const uploadedImages = await batchUploadFiles(
          pendingImages,
          'CONTRACT',
          '合同管理'
        )
        
        const uploadedUrls = []
        for (let i = 0; i < formData.contractImages.length; i++) {
          const image = formData.contractImages[i]
          if (image && typeof image === 'object' && image.status === 'pending' && image.file) {
            const uploaded = uploadedImages.find(u => {
              return u.fileName === (image.fileName || image.file.name)
            })
            if (uploaded) {
              let imageUrl = uploaded.url
              if (!imageUrl.startsWith('http')) {
                const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
                imageUrl = imageUrl.startsWith('/') ? baseURL + imageUrl : `${baseURL}/${imageUrl}`
              }
              if (image.url && image.url.startsWith('blob:')) {
                URL.revokeObjectURL(image.url)
              }
              uploadedUrls.push(imageUrl)
            }
          } else {
            uploadedUrls.push(typeof image === 'string' ? image : (image.url || image))
          }
        }
        formData.contractImages = uploadedUrls
      } catch (error) {
        ElMessage.error(`图片上传失败: ${error.message}`)
        throw error
      }
    }
    
    // 上传所有待上传的附件（使用组件的方法，自动显示进度条）
    if (attachmentUploadRef.value) {
      try {
        const uploadedAttachments = await attachmentUploadRef.value.uploadAll()
        // 更新 formData.contractAttachments（组件内部已经更新了，这里确保同步）
        formData.contractAttachments = uploadedAttachments || formData.contractAttachments
      } catch (error) {
        ElMessage.error(`附件上传失败: ${error.message}`)
        throw error
      }
    }
    
    const requestData = {
      contractNumber: formData.contractNumber,
      customerId: formData.customerId,
      contractType: formData.contractType,
      contractAmount: formData.contractAmount,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      content: formData.content || '',
      remark: formData.remark || '',
      // 合同图片：始终传数组，空数组表示清空旧图片
      contractImages: Array.isArray(formData.contractImages)
        ? formData.contractImages
        : [],
      attachmentImages: formData.attachmentImages,
      // 合同附件：始终传数组，空数组表示清空旧附件
      contractAttachments: Array.isArray(formData.contractAttachments)
        ? formData.contractAttachments.map(att => att.url || '').filter(url => url)
        : [],
      action: 'submit'
    }

    let res
    let finalContractId = isCorrectMode.value ? null : contractId.value

    // 修正模式下创建新数据
    if (isEdit.value && contractId.value && !isCorrectMode.value) {
      requestData.contractId = contractId.value
      res = await updateContract(requestData)
    } else {
      // 新建合同，先保存获取ID
      const saveData = { ...requestData }
      delete saveData.action
      const saveRes = await createContract(saveData)
      if (saveRes.code === '000000') {
        finalContractId = saveRes.data || contractId.value
        // 如果有ID，检查是否需要审批
        if (finalContractId) {
          try {
            const checkRes = await checkNeedApproval('CONTRACT', finalContractId)
            if (checkRes.code === '000000' && checkRes.data === false) {
              await ElMessageBox.alert('该流程无需审批，直接通过！', '提示', {
                type: 'info',
                confirmButtonText: '知道了'
              })
            }
          } catch (error) {
            console.error('检查审批节点失败:', error)
          }
        }
        // 继续提交审批
        requestData.contractId = finalContractId
        requestData.action = 'submit'
        res = await updateContract(requestData)
      } else {
        throw new Error(saveRes.message || '保存失败')
      }
    }

    if (res.code === '000000') {
      const msg = res.message || res.msg || ''
      if (msg.includes('未配置审批人') || msg.includes('无需审批') || msg.includes('已默认通过')) {
        await ElMessageBox.alert('该流程无需审批，直接通过！', '提示', {
          type: 'info',
          confirmButtonText: '知道了'
        })
      } else {
        ElMessage.success('提交成功')
      }
      // 构建标签路径，用于关闭标签页
      let fullPath = route.path
      if (route.path === '/home/salesman-contract-form' && route.query && route.query.id) {
        fullPath = `${route.path}?id=${route.query.id}`
      } else if (route.path === '/home/salesman-contract-form') {
        fullPath = route.path
      } else if (route.query && Object.keys(route.query).length > 0) {
        const queryString = Object.keys(route.query)
          .map(key => `${key}=${encodeURIComponent(route.query[key])}`)
          .join('&')
        fullPath = `${route.path}?${queryString}`
      }
      
      // 编辑或新增合同提交审批后，返回列表页并刷新
      // 个人用户自动切换到已通过状态，其他用户切换到待审批状态
      const status = isPersonalUser.value ? 'approved' : 'pending'
      router.push({
        path: '/home/salesman-contract',
        query: { refresh: 'true', contractStatus: status }
      }).finally(() => {
        if (closeTab) {
          closeTab(fullPath)
        }
      })
    } else {
      ElMessage.error(res.message || '提交失败')
    }
  } catch (error) {
    console.error('提交审批失败:', error)
    ElMessage.error(error.message || '提交失败')
  } finally {
    saving.value = false
    submitting.value = false
  }
}

// 取消
const handleCancel = () => {
  // 构建标签路径，需与 Home.vue 中 addTab 的 tabKey 规则一致
  // - 编辑：/home/salesman-contract-form?id=xxx
  // - 新增/修正：/home/salesman-contract-form
  let fullPath = route.path
  if (route.path === '/home/salesman-contract-form' && route.query && route.query.id) {
    fullPath = `${route.path}?id=${route.query.id}`
  } else if (route.path === '/home/salesman-contract-form') {
    fullPath = route.path
  } else if (route.query && Object.keys(route.query).length > 0) {
    const queryString = Object.keys(route.query)
      .map(key => `${key}=${encodeURIComponent(route.query[key])}`)
      .join('&')
    fullPath = `${route.path}?${queryString}`
  }
  
  // 先回到合同列表，再关闭当前标签（确保修正场景能正确关闭“新增合同”标签）
  router.push({ name: 'SalesmanContractManagement' }).finally(() => {
    if (closeTab) {
      closeTab(fullPath)
    } else {
      router.back()
    }
  })
}

onMounted(async () => {
  // 加载合同类型字典
  await loadContractTypes()

  // 编辑模式或修正模式：加载合同详情
  if (isEdit.value || isCorrectMode.value) {
    await loadContractDetail()
    // 修正模式下，生成新的合同编号（避免合同编号冲突）
    if (isCorrectMode.value) {
      formData.contractNumber = generateContractNumber()
    }
  } else {
    // 新增模式：生成合同编号
    formData.contractNumber = generateContractNumber()
  }
})
</script>

<style scoped>
.contract-form {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.contract-form-content {
  max-width: 1200px;
}

.contract-form-content :deep(.el-form-item) {
  margin-bottom: 22px;
}

.contract-form-content :deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
}

.amount-unit {
  margin-left: 10px;
  color: #1890ff;
  font-weight: 500;
  font-size: 14px;
}

.image-upload-container,
.attachment-upload-container {
  width: 100%;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}

.image-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.image-item:hover .image-actions {
  display: block;
}

.image-actions {
  position: absolute;
  top: -8px;
  right: -8px;
  display: none;
  z-index: 10;
}

.image-uploader {
  width: 120px;
  height: 120px;
}

.image-uploader :deep(.el-upload) {
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}

.image-uploader :deep(.el-upload:hover) {
  border-color: #409eff;
}

.add-image-btn {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  background-color: #fafafa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.add-image-btn .add-icon {
  font-size: 28px;
  color: #8c8c8c;
  transition: color 0.3s;
}

.add-image-btn:hover .add-icon {
  color: #409eff;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.upload-tip .tip-item {
  margin: 2px 0;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f5f7fa;
  transition: all 0.3s;
}

.attachment-item:hover {
  background: #ecf5ff;
  border-color: #b3d8ff;
}

.attachment-icon {
  font-size: 20px;
  color: #409eff;
  margin-right: 12px;
  flex-shrink: 0;
}

.attachment-name {
  flex: 1;
  color: #606266;
  font-size: 14px;
  word-break: break-all;
  line-height: 1.5;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.form-footer .el-button {
  min-width: 100px;
}

/* 客户选择器样式 */
.customer-selector {
  width: 100%;
  position: relative;
}

.customer-selector .clear-icon {
  cursor: pointer;
  color: #909399;
  transition: color 0.3s;
  margin-right: 8px;
}

.customer-selector .clear-icon:hover {
  color: #409eff;
}

.customer-selector .arrow-icon {
  color: #909399;
}

/* 客户选择弹窗样式 */
.customer-picker-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.customer-picker-content {
  display: flex;
  flex-direction: column;
  min-height: 500px;
  max-height: 600px;
}

.customer-picker-search {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.customer-picker-search .el-input {
  flex: 1;
}

.customer-picker-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 8px;
  background-color: #fafafa;
  min-height: 0; /* 确保flex子元素可以收缩 */
}

.customer-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 8px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.customer-picker-item:hover {
  border-color: #409eff;
  background-color: #ecf5ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.customer-picker-item.active {
  border-color: #409eff;
  background-color: #ecf5ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.customer-picker-item:last-child {
  margin-bottom: 0;
}

.customer-item-main {
  flex: 1;
}

.customer-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 6px;
}

.customer-meta {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 8px;
}

.customer-meta .no-contact {
  color: #c0c4cc;
}

.customer-meta .phone {
  color: #606266;
}

.check-icon {
  color: #409eff;
  font-size: 18px;
  font-weight: bold;
}

/* 上传进度条样式 */
.upload-progress-container {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.upload-progress-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  font-weight: 500;
}

.upload-progress-text {
  font-size: 12px;
  color: #909399;
  text-align: right;
  margin-top: 8px;
}

.customer-picker-pagination {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0; /* 防止分页组件被压缩 */
  overflow: visible; /* 确保分页组件内容可见 */
}

.customer-picker-pagination :deep(.el-pagination) {
  justify-content: flex-end;
  flex-wrap: wrap;
}

.customer-picker-pagination :deep(.el-pagination__sizes) {
  margin-right: 16px;
}

.customer-picker-pagination :deep(.el-pagination__total) {
  margin-right: 16px;
}

.customer-picker-pagination :deep(.el-pagination__jump) {
  margin-left: 16px;
}

/* 确保分页组件在小屏幕下也能正常显示 */
@media (max-width: 768px) {
  .customer-picker-pagination :deep(.el-pagination) {
    justify-content: center;
  }
  
  .customer-picker-pagination :deep(.el-pagination__sizes),
  .customer-picker-pagination :deep(.el-pagination__total),
  .customer-picker-pagination :deep(.el-pagination__jump) {
    margin: 8px 4px;
  }
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>


